import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Link as MuiLink,
  Paper,
} from '@mui/material';
import { Email, ArrowBack } from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

/**
 * Portal-facing forgot password — same API as staff, but copy/links stay in the portal.
 * Backend routes client-role users to /portal/reset-password.
 */
const PortalForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [sent, setSent] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(json.message || 'Unable to send reset email');
      }
      setSent(true);
      toast.success(json.message || 'If that email exists, a reset link was sent.');
    } catch (err: any) {
      setError(err.message || 'Unable to send reset email');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'background.default',
        p: 2,
      }}
    >
      <Paper sx={{ width: '100%', maxWidth: 420, p: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Email sx={{ fontSize: 40, mb: 1, color: 'primary.main' }} />
          <Typography variant="h5" fontWeight={600}>
            Forgot portal password
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            We will email a reset link if a portal account exists for that address.
            Your firm admin can also reset access from Clients.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {sent ? (
          <Box>
            <Alert severity="success" sx={{ mb: 2 }}>
              If an account exists for that email, a reset link has been sent. Check your inbox
              (and spam folder).
            </Alert>
            <Button fullWidth variant="contained" onClick={() => navigate('/portal/login')}>
              Back to portal login
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              required
              type="email"
              label="Portal email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
              sx={{ mb: 2 }}
            />
            <Button type="submit" fullWidth variant="contained" disabled={isLoading || !email.trim()}>
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Send reset link'}
            </Button>
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <MuiLink component={Link} to="/portal/login" underline="hover">
                <ArrowBack sx={{ fontSize: 14, verticalAlign: 'middle', mr: 0.5 }} />
                Back to portal login
              </MuiLink>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default PortalForgotPasswordPage;
